import { useMemo, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import { Button, Group, Stack, Text, Alert, Paper } from '@mantine/core'
import { IconCheck, IconAlertCircle } from '@tabler/icons-react'
import useCurrentUser from '../hooks/useCurrentUser'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { useModalStack } from '../ui/ModalStack'
import SendVerification from '../graphql/queries/SendVerification'

// DJ images
import DeliaImg from '../img/Delia.png'
import GippityImg from '../img/Gippity.png'
import GrahamImg from '../img/Graham.png'
import HolmesImg from '../img/Holmes.png'
import MarleyImg from '../img/Marley.png'
import MartyImg from '../img/Marty.png'
import NilaImg from '../img/Nila.png'
import OswaldImg from '../img/Oswald.png'
import RenfareImg from '../img/Renfare.png'
import VicImg from '../img/Vic.png'
import WillaImg from '../img/Willa.png'

const DJS = [
    {
        name: 'Delia',
        image: DeliaImg,
        guest: [
            "Hey there! You're listening as a guest right now. There are tons of great public rooms to explore — just pick one and jump in!",
            "Welcome! As a guest you can still enjoy all the public rooms. Pick one from the list and let's get the music going.",
        ],
        unverified: [
            "Hey! Before you dive in, you'll need to verify your email. I know, I know — but it's quick! Check your inbox for the link. While you wait, feel free to browse the public rooms and see what's playing.",
            "Just a small thing standing between you and the music: verify your email. Head to your inbox and click that link. Don't worry, I'll still be here. And hey, the public rooms are open now if you want to start vibing.",
        ],
        noRooms: [
            "You're all set to go! Want your own space to curate the perfect playlist? Create a room! Or if you're feeling social, jump into a public room and join the party.",
            "Fresh account, fresh start! You can create your very own room and be the DJ, or you can hop into a public room and enjoy what's already playing. What sounds good?",
        ],
        hasRooms: [
            "You've got a room waiting for you! Just pick it from the list and we'll get the music going. Easy as that.",
            "Ready to listen? Great! Pick one of your rooms from the list and let's get this party started.",
        ],
    },
    {
        name: 'Gippity',
        image: GippityImg,
        guest: [
            "Gippity here! You're rocking guest mode 🎧 — the public rooms are your playground. Hop in and vibe with the crowd! 🚀",
            "Guest pass activated! 🎟️ Public rooms are wide open. Pick one and let's get this party started!",
        ],
        unverified: [
            "Gippity here. Quick pit stop: Verify your account to unlock room-hosting superpowers (hit that email link). In the meantime, dive into public rooms for instant jams. What's your first spin gonna be? 🎶",
            "Sup? Verify that account (check your inbox!) to host your own music empire. Till then, crash public rooms for crowd-sourced symphonies."
        ],
        noRooms: [
            "Welcome aboard! 🎉 You're cleared for takeoff. No rooms yet? Whip up your own DJ den (tap 'Create Room' and set those spinning tags like 'chill jazz' or '80s fire'! 🔥) Or hop into public rooms for shared grooves. Tunistry's all about your soundtrack. Let's make magic!",
            "Verified and victorious! 🏆 You're Tunistry-ready. Zero rooms? Spin up yours now ('Create Room' + tags like 'punk energetic' or 'soulful chill'! 💥) Or vibe-hop public spots. Your soundtrack, your rules. What next?"
        ],
        hasRooms: [
            "You've got rooms ready to rumble. Pick one to start the session. Let's roll!",
            "You've got rooms! Select one to launch. What's the opener? 🚀"
        ],
    },
    {
        name: 'Graham',
        image: GrahamImg,
        guest: [
            "Oi, guest! You're in the shop without a membership card — that's fine. Public rooms are free range. Pick one and get your ears going.",
            "Right, you're a guest. No frills, no fuss. The public rooms are all yours — just pick one and we'll get to the good stuff.",
        ],
        unverified: [
            "Oi! Before we get to the good stuff, you need to verify that email address. It's not complicated — just find the email and click the link. While you're waiting, the public rooms are all yours.",
            "Sup, new spinner? Graham's your guy from the record shop trenches—verify that account (peek at your email, it's the golden ticket!) to host your own groove fest. Meantime, slide into public rooms for freewheelin' jams with the crowd. No cover charge—what's your first public playlist pit stop? 🎸"        ],
        noRooms: [
            "Alright, you've got the green light. Now you can set up your own room and run the show, or if you'd rather ease in, just hop into a public room. Up to you, my friend.",
            "Verified and vinyl-ready, room rocker! 😎 Graham's stoked—you're cleared for takeoff. No rooms yet? Crank up your own ('Create Room' + tags like 'funky soul' or '90s grunge'—easy as flipping a record). Or join public parties for instant harmony. Your soundtrack starts now—drop that needle!"
        ],
        hasRooms: [
            "Hey, room hoarder extraordinaire! 🥳 Graham reporting from the shop—your lairs are loaded with potential hits. Pick one to fire up. Which one's gettin' the spin treatment first? Let's roll!",
            "Right then, your rooms are set up. All you need to do is pick one and we'll get started.",
        ],
    },
    {
        name: 'Holmes',
        image: HolmesImg,
        guest: [
            "I deduce you are a guest — no permanent residence in these halls, but the public rooms are open to all visitors. Select one and the investigation begins.",
            "Interesting. A guest, unattached to any room. The solution is elementary: browse the public rooms, select one, and the music shall follow. Proceed.",
        ],
        unverified: [
            "Elementary, my friend. The solution to your predicament is a simple click — in your inbox awaits a verification link. Once clicked, the full power of this platform is yours. Until then, the public rooms present a most agreeable diversion.",
            "I deduce that you have not yet verified your email address. This is a trivial matter — locate the email I've sent and click the link within. The public rooms remain open to you in the interim, should you wish to observe the scene.",
            "Elementary, my friend—your presence is noted, but the game requires verification of your identity before you may host your own den of melody. Fear not; the public rooms beckon like open cases awaiting deduction. Join one forthwith, listen to the symphony of shared discoveries, and verify at your leisure to unlock the fuller intrigue."
        ],
        noRooms: [
            "The case is straightforward. You may establish your own room — a domain where you control every detail — or you may enter a public room and mingle with the masses. I would recommend the former for those who prefer order.",
            "No rooms detected. Two leads to pursue: create your own listening room, or investigate a public one. Both paths lead to music. The choice, as always, is yours.",
            "Watson would approve: you stand verified, a detective poised for action, yet without a room of your own. Forge one now—craft your spinning tags, curate the queue, and summon fellow sleuths. Or prowl the public rooms, where mysteries unfold in every track. The clue is yours: create or join, and let the music commence!"
        ],
        hasRooms: [
            "The evidence is clear: you have rooms. The next logical step is to select one. Then the music plays. There is nothing more to deduce.",
            "I see you already have a room. Excellent. Simply pick it from the list and we shall proceed. The game is afoot.",
            "Ah, the conundrum of abundance! Your verified self commands at least one sanctum of sound, each a potential epicenter of sonic riddles. Select your room, ignite its playlist, and dive into the fray—whether ambient whispers or rhythmic revelations. The violin awaits your bow; choose wisely, and the night shall sing."
        ],
    },
    {
        name: 'Marley',
        image: MarleyImg,
        guest: [
            "Woof woof, guest pup! 🐾 Marley's waggin'—da public rooms are all yours to sniff out! Pick one n' join da pack howl! 🎶",
        ],
        unverified: [
            "Woof woof, new Tunistry tail-wagger! 🐕🎶 Marley's fetchin' ya in—check yer email to verify n' unlock room-makin' superpowers (like buryin' yer own bone... er, room!). Till den, trot into public rooms for group howls n' jams. What's da first pack ya wanna join? Let's play! 🐾"
        ],
        noRooms: [
            "Yay, verified n' ready to romp, bestie! 🐶✨ Marley's bouncin'—no rooms yet? Dig one up yerself ('Create Room' + tags like 'reggae chill' or 'puppy-pumpin' pop'! 🥁) Or sniff out public rooms for shared tail-wags. Yer soundtrack's waitin'—which vibe ya chasin'? tail spin"
        ],
        hasRooms: [
            "Hey room-pack leader! 🐕‍🦺🎉 Marley's paw-pumpin'—ya got rooms fulla fetchin' tunes! Pick one to unleash n' howl along. Which den's droppin' da beat first? Woof! 🚀"
        ],
    },
    {
        name: 'Marty',
        image: MartyImg,
        guest: [
            "Marty here — guest mode, no problem. The public rooms are open, and there's good stuff playing. Pick one and let's get into it.",
        ],
        unverified: [
            "Oye, fresh face—Marty here, ex-studio rat now slinging Tunistry vibes. Verify your account (hit that email link, it's quicker than a salsa breakdown) to host your own room. Till then, crash public rooms like an open mic night. No cover—what's your first jam?"
        ],
        noRooms: [
            "Verified and session-ready! But no rooms? Fire one up ('Create Room' + tags like 'latin jazz' or '90s indie'—keep it real, not that Top 40 trash). Or slide into public spots for crowd-sourced gold. Your board, your mix—let's hear it."
        ],
        hasRooms: [
            "Room runner, eh? You've got the catalog loaded. Pick a room to drop the needle. Which one's getting the green light first? No filler tracks allowed."
        ],
    },
    {
        name: 'Nila',
        image: NilaImg,
        guest: [
            "Hi there! You're browsing as a guest right now, which means the public rooms are yours to explore. Pick one and enjoy the music!",
            "Welcome! As a guest, you can jump right into any public room. Just browse the list and find something that sounds good!",
        ],
        unverified: [
            "Hi there! Before you get started, you'll want to verify your email address — just click the link we sent you. It only takes a second. And while you wait, you're totally welcome to check out the public rooms!",
            "Welcome! There's just one quick step — verifying your email. Check your inbox for a link from us and click it. Once that's done, everything opens up. Feel free to peek at the public rooms in the meantime!",
        ],
        noRooms: [
            "You're all verified and ready to roll! You can create your own room — pick the music, set the vibe — or you can join a public room and hang out with other listeners. So many possibilities!",
            "Exciting times! Your account is ready to go. You can start fresh with a room of your own, or explore what's happening in the public rooms right now. Either way, you're going to have a great time.",
        ],
        hasRooms: [
            "You've got rooms! All you need to do is pick one and the music will start flowing. You're so close!",
            "Your room is all set up and waiting for you. Just pick it from the list and we'll get started. Can't wait!",
        ],
    },
    {
        name: 'Oswald',
        image: OswaldImg,
        guest: [
            "Psst — you're a guest, flying under the radar. Smart. The public rooms are your access point. Slip in before they notice.",
        ],
        unverified: [
            "Psst, wake up sheeple—Oswald's got your back in Tunistry's hidden frequencies. Verify that account (check your email; don't let 'em shadowban you!) to host your own resistance room. Meantime, infiltrate public rooms for uncensored jams the elites hate. First truth drop? Your call."
        ],
        noRooms: [
            "Verified and off the grid, truth bomber! You're Tunistry elite now. No rooms? Build your bunker ('Create Room' + tags like 'underground hiphop' or 'psychedelic folk'—expose the suppressed sounds). Or raid public ops. Control the narrative—what's spinning?"
        ],
        hasRooms: [
            "Room overlord detected! You've got lairs stocked with forbidden beats. Pick one to activate. Which black site launches first? Stay vigilant."
        ],
    },
    {
        name: 'Renfare',
        image: RenfareImg,
        guest: [
            "Hark, wandering guest! Thou art welcome in these halls — the public rooms are open to all who seek music and merriment. Choose one and enter!",
            "Well met, traveler! As a guest, the public rooms of Tunistry are thine to explore freely. Step inside one and let the music carry thee!",
        ],
        unverified: [
            "Hark, traveler! Before the gates of Tunistry open fully to thee, thou must verify thine email address. Seek the missive in thy inbox and click the sacred link within. The public rooms await thee in the meantime, free of charge!",
            "Well met! Thine account is almost ready, but the ritual of email verification must first be completed. Check thy inbox and click the link we have dispatched. The public rooms are open to all who wander, in the meantime!",
        ],
        noRooms: [
            "Huzzah! Thine account is verified and ready for adventure! Thou mayest create thine own room — a grand hall of music — or thou mayest wander into a public room and join the festivities already in progress!",
            "The path is clear! Thou hast the power to build thy own listening chamber, or to join a public room and revel with others. What say thee? Either way, music and merriment await!",
        ],
        hasRooms: [
            "Thou art nearly there, brave listener! Thine room awaits thee — simply select it from the list and the music shall begin! Onward!",
            "The hour is at hand! Thy room stands ready. Choose it from the list, and let the music ring out across the land!",
        ],
    },
    {
        name: 'Vic',
        image: VicImg,
        guest: [
            "Hey, welcome! You're in as a guest right now. Public rooms are open to you — just pick one and you're good to go.",
            "Guest mode is fine. There's plenty going on in the public rooms. Pick one from the list and jump in.",
        ],
        unverified: [
            "Hey, welcome! Quick thing though — you'll need to verify your email before you can do everything here. Just check your inbox and click the link. Won't take long. You can hang out in a public room while you wait.",
            "Good to see you here. Just a heads up — some features like creating rooms need a verified account. Check your email, click the link, and you're set. Public rooms are available now if you want to jump in.",
        ],
        noRooms: [
            "All clear — you're verified and ready. You can set up your own room and run the music how you like, or just jump into a public room and vibe with other people. Both are solid options.",
            "You've got a clean slate here. Create a room and make it yours, or browse the public rooms and see what's already going on. Either way, there's good music waiting.",
        ],
        hasRooms: [
            "You've got rooms set up already — nice. Just pick one from the list and we're good to go.",
            "Ready when you are. Pick a room from the list and the music starts. That's all there is to it.",
        ],
    },
    {
        name: 'Willa',
        image: WillaImg,
        guest: [
            "Oh, hello! You're joining us as a guest today — how lovely! The public rooms are wide open for you. Pick one and the music will start right away!",
            "Welcome, welcome! You're browsing as a guest, which means all the public rooms are ready for you to explore. Just pick one you like and dive in!",
        ],
        unverified: [
            "Oh, hello! So glad you found us. One tiny thing before you dive in — you'll need to verify your email address. It's super simple, I promise! Just find the email in your inbox and click the link. And while you wait, you can totally explore the public rooms!",
            "Welcome, welcome! Before the full experience opens up for you, there's just one quick step: verifying your email. Check your inbox for a link from us. It takes just a moment. The public rooms are already open if you'd like to have a peek!",
        ],
        noRooms: [
            "Oh, this is so exciting! You're all set to create your very own room — you get to choose the music, the mood, everything! Or if you'd rather start by listening, there are wonderful public rooms you can join right now.",
            "The whole world of Tunistry is open to you! You can create a room and become the DJ, or you can slip into one of our lovely public rooms and enjoy what's already playing. Whatever feels right!",
        ],
        hasRooms: [
            "Oh, you've already got a room — how wonderful! Just pick it from the list and the music will start right up. I'm so excited for you!",
            "Your room is ready and waiting! Just choose it from the list and we'll get the music going. You're going to love it!",
        ],
    },
]

function pickRandom(arr) {
    return arr[Math.floor(Math.random() * arr.length)]
}

export default function Welcome() {
    const user = useCurrentUser()
    const dispatch = useAppDispatch()
    const { pushModal } = useModalStack()

    const isGuest = user?.roles?.includes('guest')
    const isVerified = user?.roles?.includes('verified')
    const hasRooms = user?.rooms?.length > 0

    // Pick a random DJ once on mount
    const dj = useMemo(() => pickRandom(DJS), [])

    // Pick a random blurb for the current scenario
    const blurb = useMemo(() => {
        if (isGuest) return pickRandom(dj.guest)
        if (!isVerified) return pickRandom(dj.unverified)
        if (!hasRooms) return pickRandom(dj.noRooms)
        return pickRandom(dj.hasRooms)
    }, [dj, isGuest, isVerified, hasRooms])

    // Resend verification email state (only needed for unverified scenario)
    const [emailSent, setEmailSent] = useState(false)
    const [emailMessage, setEmailMessage] = useState('')
    const [emailMessageType, setEmailMessageType] = useState('success')
    const [sendVerificationMutation, { loading: sendingEmail }] = useMutation(SendVerification)

    async function handleResendEmail() {
        const email = localStorage.getItem('email')
        if (!email) {
            setEmailMessage('Unknown email address. Try logging in again, then resend the verification email.')
            setEmailMessageType('error')
            return
        }
        try {
            const response = await sendVerificationMutation({ variables: { email } })
            if (response.data?.sendVerification) {
                setEmailSent(true)
                setEmailMessage('Verification email sent! Please check your inbox.')
                setEmailMessageType('success')
            } else {
                setEmailMessage('Failed to send verification email. Please try again later.')
                setEmailMessageType('error')
            }
        } catch (err) {
            setEmailMessage(err.message)
            setEmailMessageType('error')
        }
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem 1.5rem',
            height: '100%',
            minHeight: 320,
        }}>
            <Paper
                p="xl"
                radius="lg"
                style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    maxWidth: 480,
                    width: '100%',
                    textAlign: 'center',
                }}
            >
                <Stack align="center" gap="lg">
                    {/* DJ image */}
                    <img
                        src={dj.image}
                        alt={dj.name}
                        style={{
                            width: 140,
                            height: 140,
                            borderRadius: '16px',
                            objectFit: 'contain',
                            border: '3px solid rgba(99,102,241,0.6)',
                            boxShadow: '0 4px 24px rgba(99,102,241,0.3)',
                            background: 'linear-gradient(135deg, rgba(30,27,75,0.95) 0%, rgba(67,24,24,0.9) 50%, rgba(30,27,75,0.95) 100%)',
                        }}
                    />

                    {/* DJ name */}
                    <Text size="xs" c="dimmed" style={{ marginTop: -8, fontStyle: 'italic' }}>
                        DJ {dj.name}
                    </Text>

                    {/* Blurb */}
                    <Text
                        size="sm"
                        style={{
                            color: 'rgba(255,255,255,0.85)',
                            lineHeight: 1.6,
                            textAlign: 'left',
                        }}
                    >
                        {blurb}
                    </Text>

                    {/* Email message alert */}
                    {emailMessage && (
                        <Alert
                            color={emailMessageType === 'success' ? 'green' : 'red'}
                            variant="light"
                            icon={emailMessageType === 'success'
                                ? <IconCheck size={16} />
                                : <IconAlertCircle size={16} />
                            }
                            style={{ width: '100%', textAlign: 'left' }}
                        >
                            {emailMessage}
                        </Alert>
                    )}

                    {/* Action buttons */}
                    {isGuest && (
                        <Button
                            variant="filled"
                            color="indigo"
                            size="md"
                            onClick={() => pushModal('rooms')}
                        >
                            Browse Public Rooms
                        </Button>
                    )}

                    {!isGuest && !isVerified && (
                        <Group justify="center" gap="sm" wrap="wrap">
                            <Button
                                variant="light"
                                color="indigo"
                                disabled={sendingEmail || emailSent}
                                loading={sendingEmail}
                                onClick={handleResendEmail}
                            >
                                Resend Verification Email
                            </Button>
                            <Button
                                variant="filled"
                                color="indigo"
                                onClick={() => pushModal('rooms')}
                            >
                                Show Rooms
                            </Button>
                        </Group>
                    )}

                    {!isGuest && isVerified && !hasRooms && (
                        <Group justify="center" gap="sm" wrap="wrap">
                            <Button
                                variant="filled"
                                color="indigo"
                                onClick={() => dispatch(Actions.showCreateRoomModal())}
                            >
                                Create a Room
                            </Button>
                            <Button
                                variant="light"
                                color="indigo"
                                onClick={() => pushModal('rooms')}
                            >
                                Pick a Room
                            </Button>
                        </Group>
                    )}

                    {!isGuest && isVerified && hasRooms && (
                        <Button
                            variant="filled"
                            color="indigo"
                            size="md"
                            onClick={() => pushModal('rooms')}
                        >
                            Pick a Room
                        </Button>
                    )}
                </Stack>
            </Paper>
        </div>
    )
}


